import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { 
  restoreCartFromBackup, 
  clearCartBackup, 
  isPaymentInProgress,
  markPaymentCompleted,
  cleanupExpiredBackups
} from '../helper-functions/cartBackupUtils';
import { setCartList } from '../redux/slices/cart';
import { useRouter } from '../helper-functions/useRouterReplacement';

/**
 * Hook to handle cart restoration after payment failure
 * Should be used in the main App component or layout
 */
export const useCartRestoration = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    // Clean up expired backups first
    cleanupExpiredBackups();

    // Check if we're returning from a payment flow
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');
    const orderStatus = urlParams.get('order_status');
    
    // Check if payment was in progress
    if (isPaymentInProgress()) {
      // If we have payment status parameters, handle accordingly
      if (paymentStatus === 'failed' || paymentStatus === 'cancelled' || orderStatus === 'failed') {
        // Payment failed, restore cart
        const restoredCart = restoreCartFromBackup();
        if (restoredCart && restoredCart.length > 0) {
          dispatch(setCartList(restoredCart));
          toast.error('Payment failed. Your cart has been restored.');
          console.log('Cart restored after payment failure:', restoredCart);
        }
        clearCartBackup();
        markPaymentCompleted();
      } else if (paymentStatus === 'success' || paymentStatus === 'completed') {
        // Payment successful, clear backup
        clearCartBackup();
        markPaymentCompleted();
        toast.success('Payment completed successfully!');
      } else {
        // No clear payment status, but payment was in progress
        // This might happen if user manually navigated back
        // Restore cart to be safe
        const restoredCart = restoreCartFromBackup();
        if (restoredCart && restoredCart.length > 0) {
          dispatch(setCartList(restoredCart));
          toast.info('Your cart has been restored.');
          console.log('Cart restored after navigation back:', restoredCart);
        }
        markPaymentCompleted();
      }
    }

    // Handle specific routes that indicate payment failure
    if (router.pathname === '/checkout-failed' || router.pathname.includes('payment-failed')) {
      const restoredCart = restoreCartFromBackup();
      if (restoredCart && restoredCart.length > 0) {
        dispatch(setCartList(restoredCart));
        toast.error('Payment failed. Your cart has been restored.');
      }
      markPaymentCompleted();
    }

    // Handle order success page - clear backup
    if (router.pathname === '/order-success' || router.pathname.includes('order-success')) {
      clearCartBackup();
      markPaymentCompleted();
    }

  }, [dispatch, router.pathname]);

  // Function to manually restore cart (can be called from UI)
  const manuallyRestoreCart = () => {
    const restoredCart = restoreCartFromBackup();
    if (restoredCart && restoredCart.length > 0) {
      dispatch(setCartList(restoredCart));
      toast.success('Cart restored successfully!');
      clearCartBackup();
      return true;
    } else {
      toast.error('No cart backup found to restore.');
      return false;
    }
  };

  return {
    manuallyRestoreCart
  };
};

export default useCartRestoration;